import "./emoji-slider.css";

import { LiveLikeEmojiSlider } from "@livelike/engagementsdk";
import { t } from "i18next";
const html = (window as any).html;

class SBSEmojiSlider extends LiveLikeEmojiSlider {
  @((window as any).property({ type: Boolean })) userInteracted = false;

  firstUpdated() {
    super.firstUpdated();
    this.updateComplete.then(() => {
      // If there's no interaction from API yet, try to restore from localStorage
      const widgetId = (this as any).widgetPayload?.id;
      try {
        if (!this.interaction) {
          if (widgetId) {
            const key = `sbs_widget_interaction_${widgetId}`;
            const raw = localStorage.getItem(key);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (
                parsed &&
                parsed.type === "emoji-slider" &&
                typeof parsed.magnitude === "number"
              ) {
                const restoredVal = Math.round(parsed.magnitude * 100);
                if (this.inputEl) {
                  this.inputEl.value = String(restoredVal);
                  // Update internal value and UI
                  this.updateVal();
                } else {
                  // Fallback: set val directly if input isn't mounted yet
                  (this as any).val = restoredVal;
                }
                this.disabled = true;
                this.userInteracted = true;
              }
            }
          }
        }
      } catch {
        console.log(
          "Error reading emoji slider from localStorage for ",
          widgetId
        );
      }
    });
  }

  updateVal = () => {
    if (this.inputEl) {
      let newVal = +this.inputEl.value;
      if (this.val !== newVal) {
        this.inputContainer.style.setProperty("--val", (this.val = newVal));
        this.options = this.options.map((item: any, idx: number) => ({
          ...item,
          mag: idx === 0 ? 0 : (idx / (this.options.length - 1)) * 100,
          max: (idx / this.options.length) * 100,
        }));
        for (let item of this.options) {
          if (this.val >= item.max && this.imageEl.src !== item.image_url) {
            this.imageEl.src = item.image_url;
            this.setThumbWidth();
          }
        }
      }
    }
  };

  fetchLatestInteraction() {
    fetch(this.widgetPayload.url)
      .then((r) => r.json())
      .then((updatedWidget) => {
        this.widgetPayload = updatedWidget;

        if (updatedWidget.average_magnitude) {
          this.average_magnitude = updatedWidget.average_magnitude;
        }
      });
  }

  submitVote(vote: number) {
    this.lockInVote(vote);
    // Save submission locally on click as a fallback
    const widgetId = (this as any).widgetPayload?.id;
    try {
      if (widgetId) {
        const key = `sbs_widget_interaction_${widgetId}`;
        localStorage.setItem(
          key,
          JSON.stringify({
            type: "emoji-slider",
            widgetId,
            magnitude: vote,
            timestamp: Date.now(),
          })
        );
      }
    } catch {
      console.log("Error saving emoji slider to localStorage for ", widgetId);
    }
    this.fetchLatestInteraction();
  }

  render() {
    const interactionMagnitude =
      this.interaction?.magnitude ?? this.widgetPayload.initial_magnitude;
    const initialMag = Math.round(interactionMagnitude * 100);
    const averageMagnitudePercentage = Math.round(this.average_magnitude * 100);

    // Determine if result mark should be shown
    const shouldShowResultMark =
      this.phase !== "ready" &&
      this.phase !== "interactive" &&
      String(this.val) !== "null";

    let resultMark = null;

    if (shouldShowResultMark) {
      const leftPercent =
        averageMagnitudePercentage !== 100 ? averageMagnitudePercentage : 99;

      let offsetLeft = -28;
      if (averageMagnitudePercentage === 100) offsetLeft = -35;
      else if (averageMagnitudePercentage === 0) offsetLeft = -18;

      resultMark = html`
        <div class="result-mark" style="left: calc(${leftPercent}%)">
          <div class="result-average" style="left: ${offsetLeft}px">
            ${t("timeline.userAvg")}
          </div>
        </div>
      `;
    }

    return html`
      <template>
        <livelike-widget-root class="custom-widget">
          <livelike-widget-header class="widget-header" slot="header">
            <livelike-title class="custom-title"></livelike-title>
          </livelike-widget-header>
          <livelike-widget-body>
            <form style="--val: ${initialMag};" class="input-form">
              <div class="input-container">
                <input
                  type="range"
                  class="slider-input"
                  ?disabled="${this.disabled}"
                  value="${initialMag}"
                  @input=${() => (this.userInteracted = true)}
                />
                ${resultMark}
              </div>
              <output class="slider-thumb">
                <img class="slider-image" />
              </output>
            </form>
            <livelike-footer class="${this.disabled ? "disabled" : ""}">
              <button
                class="vote-button"
                @click=${() => this.submitVote(this.val / 100)}
                ?disabled="${this.sliderVoteSubmitted ||
                this.voteDisable ||
                this.disabled ||
                !this.userInteracted}"
              >
                ${!this.disabled
                  ? t("timeline.submit")
                  : this.interaction
                  ? t("timeline.submitted")
                  : t("timeline.submitted")}
              </button>
            </livelike-footer>
          </livelike-widget-body>
        </livelike-widget-root>
      </template>
    `;
  }
}
customElements.define("sbs-emoji-slider", SBSEmojiSlider as any);
